import React, { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { Modal, Select, Alert, Spinner } from "./ui";
import { API_BASE_URL } from "../services/config";
import AuthService from "../services/AuthService";

const GoogleAuthButton = () => {
  const [showModal, setShowModal] = useState(false);
  const [tipoUsuario, setTipoUsuario] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const tipos = [
    { label: "Corredor", value: "Corredor" },
    { label: "Organizador", value: "Organizador" },
  ];

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await fetch(API_BASE_URL + "/core/api/auth/google/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: credentialResponse.credential,
          tipo_usuario: tipoUsuario,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || "Erro na autenticação com o servidor");
      }

      const data = await response.json();
      const { access, is_profile_complete } = data;

      if (access) {
        localStorage.setItem("access_token", access);
      }

      if (data.token) {
        sessionStorage.setItem("brv-user-token", data.token);
        sessionStorage.setItem("brv-user", data.first_name || "");
        sessionStorage.setItem("brv-user-id", data.user_id || "");
        sessionStorage.setItem(
          "brv-tipo-user",
          data.tipo_usuario || tipoUsuario
        );
      } else if (access) {
        sessionStorage.setItem("brv-user-token", access);
        sessionStorage.setItem("brv-user", data.first_name || "");
        sessionStorage.setItem("brv-user-id", data.user_id || "");
        sessionStorage.setItem(
          "brv-tipo-user",
          data.tipo_usuario || tipoUsuario
        );
      }

      setShowModal(false);

      let isProfileComplete = false;

      if (is_profile_complete !== undefined) {
        isProfileComplete = is_profile_complete;
      } else {
        isProfileComplete = Boolean(data.cpf && (data.telefone || data.telefone1));
      }

      // Independentemente do que vier no payload inicial, a fonte de verdade
      // para saber se o perfil está completo é ter as informações adicionais.
      const userIdLogado = data.user_id || sessionStorage.getItem("brv-user-id");
      if (userIdLogado) {
        try {
          const infoRes = await new AuthService().getInfoAdicionalUsuario(userIdLogado);
          if (infoRes.ok) {
            const infoData = await infoRes.json();
            // O endpoint pode retornar objeto, array ou resposta paginada
            const infoItem = Array.isArray(infoData)
              ? infoData[0]
              : infoData?.results?.[0] || infoData;
            isProfileComplete = Boolean(
              infoItem?.cpf && (infoItem?.telefone || infoItem?.telefone1)
            );
          } else {
            isProfileComplete = false;
          }
        } catch (err) {
          console.warn("Não foi possível verificar as informações adicionais do usuário:", err);
          isProfileComplete = false;
        }
      } else {
        isProfileComplete = false;
      }

      if (isProfileComplete) {
        const redirectPath =
          (data.tipo_usuario || tipoUsuario) === "Organizador"
            ? "/dashboard"
            : "/minhas-corridas";
        window.location.href = redirectPath;
      } else {
        window.location.href = "/completar-perfil";
      }
    } catch (error) {
      console.error("Erro na autenticação com o backend:", error);
      setErrorMessage(error.message || "Falha ao conectar com o serviço de autenticação.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setErrorMessage("");
          setShowModal(true);
        }}
        className="w-full py-3 px-4 rounded-xl flex items-center justify-center gap-3 border border-slate-800 bg-slate-900/60 hover:bg-slate-800 text-white font-medium text-sm transition-all cursor-pointer shadow-sm"
      >
        <svg width="18" height="18" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
          />
        </svg>
        Entrar com Google
      </button>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setTipoUsuario("");
          setErrorMessage("");
        }}
        title="Escolha o tipo de usuário"
        maxWidth="max-w-md"
      >
        <div className="flex flex-col gap-4 py-2">
          {errorMessage && (
            <Alert variant="danger" title="Erro no login">
              {errorMessage}
            </Alert>
          )}

          <div className="flex flex-col gap-2">
            <label
              htmlFor="tipoUsuarioGoogle"
              className="font-medium text-xs sm:text-sm text-slate-300"
            >
              Como você deseja entrar na plataforma?
            </label>
            <Select
              id="tipoUsuarioGoogle"
              value={tipoUsuario}
              onChange={(e) => {
                const val = e && e.target ? (typeof e.target.value === "object" ? e.target.value.value : e.target.value) : (e?.value || "");
                setTipoUsuario(val || "");
              }}
              options={tipos}
              optionLabel="label"
              placeholder="Escolha seu tipo de usuário"
            />
          </div>

          {tipoUsuario && (
            <div className="flex flex-col items-center justify-center mt-3 gap-3">
              <span className="text-xs text-slate-400">
                Agora, faça login com sua conta Google:
              </span>
              {loading ? (
                <div className="flex items-center gap-2 text-urb-primary py-2">
                  <Spinner size="sm" />
                  <span className="text-sm">Autenticando...</span>
                </div>
              ) : (
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => {
                    console.error("Google Login falhou");
                    setErrorMessage("Não foi possível autenticar com o Google. Tente novamente.");
                  }}
                  useOneTap={false}
                />
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default GoogleAuthButton;